/**
 * "Replace video": a file picker and a drop zone, the rules for a good intro
 * video, and the upload pipeline the admin watches (spec 2026-10-23 §5).
 *
 * Accessibility: the picker is a real button; the drop zone is an extra, never
 * the only way. Progress is a labelled progressbar plus a polite live region
 * that speaks in 10 % steps (a screen reader that read every percent would
 * read nothing else). A refusal is an alert. When the video is ready, the live
 * region says so; focus moves to the "ready" message only when focus is on
 * nothing or already in this panel (useReadyFocus) — never away from a field
 * the admin is typing in.
 *
 * Reused by the Abby welcome tab (video mode): it knows nothing about the
 * intro, only "a file in, a converted video out".
 */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FinalizedMedia } from '../lib/intro-state';
import { processingText, readyText, spokenText, uploadingText, type UploadState } from '../lib/upload-flow';
import { ACCEPT_ATTRIBUTE, ACCEPTED_FILES_TEXT, UPLOAD_RULES } from '../lib/video-client-checks';
import { UploadFailure } from './UploadFailure';
import { useReadyFocus } from '../useReadyFocus';
import { useVideoUpload } from '../useVideoUpload';

interface Props {
  apiUrl: string;
  /** Used in labels: "Replace the English video". */
  languageName: string;
  hasVideo: boolean;
  /** True while the converted video waits in the draft (not yet published). */
  showReady: boolean;
  disabled: boolean;
  onReady: (media: FinalizedMedia) => void;
  onBusyChange?: (busy: boolean) => void;
  /**
   * Start of the element ids ("<idPrefix>-english-rules"). Each tab keeps its
   * panels mounted, so two panels for the same language need different
   * prefixes. The default keeps the intro tab's ids.
   */
  idPrefix?: string;
}

function statusText(s: UploadState): string {
  switch (s.phase) {
    case 'checking':
      return `Checking ${s.fileName}…`;
    case 'uploading':
      return uploadingText(s.loaded, s.total);
    case 'processing':
      return processingText(s.percent);
    default:
      return '';
  }
}

function percentOf(s: UploadState): number | null {
  if (s.phase === 'uploading') return s.total > 0 ? Math.floor((s.loaded / s.total) * 100) : 0;
  if (s.phase === 'processing') return s.percent;
  return null;
}

export function VideoUploadPanel({ apiUrl, languageName, hasVideo, showReady, disabled, onReady, onBusyChange, idPrefix = 'upload' }: Props) {
  const { state, start, cancel, retry, busy } = useVideoUpload(apiUrl, onReady);
  const input = useRef<HTMLInputElement>(null);
  const { panelRef, readyRef } = useReadyFocus(state.phase, showReady);
  const [dragging, setDragging] = useState(false);
  const blocked = disabled || busy;
  const ids = `${idPrefix}-${languageName.toLowerCase()}`;

  useEffect(() => onBusyChange?.(busy), [busy, onBusyChange]);

  const pick = (files: FileList | null) => {
    const f = files?.[0];
    if (f && !blocked) void start(f);
  };

  const percent = percentOf(state);
  // Warnings belong to the file in hand; once its video is published or discarded, they go.
  const settled = state.phase === 'idle' || state.phase === 'checking' || (state.phase === 'done' && !showReady);
  const warnings = settled ? [] : state.warnings;

  return (
    <div ref={panelRef} className="space-y-3">
      <div
        onDragOver={(e) => {
          if (blocked) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          pick(e.dataTransfer.files);
        }}
        className={cn(
          'rounded-lg border-2 border-dashed p-4 text-center transition-colors',
          dragging ? 'border-primary bg-primary/5' : 'border-border',
          blocked && 'opacity-60',
        )}
      >
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={blocked}
          aria-describedby={`${ids}-types ${ids}-rules`}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {hasVideo ? `Replace the ${languageName} video` : `Add the ${languageName} video`}
        </button>
        <p className="mt-2 text-xs text-muted-foreground">or drop a file here</p>
        <input
          ref={input}
          type="file"
          accept={ACCEPT_ATTRIBUTE}
          className="hidden"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            pick(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      <p id={`${ids}-types`} className="text-xs font-medium">
        {ACCEPTED_FILES_TEXT}
      </p>
      <ul id={`${ids}-rules`} className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
        {UPLOAD_RULES.map((rule) => (
          <li key={rule}>{rule}</li>
        ))}
      </ul>

      <p className="sr-only" aria-live="polite">
        {state.phase === 'done' && !showReady ? '' : spokenText(state, languageName)}
      </p>

      {busy && (
        <div className="space-y-2 rounded-md border bg-muted/40 p-3">
          <p className="text-sm">{statusText(state)}</p>
          {percent !== null && (
            <div
              role="progressbar"
              aria-label={state.phase === 'uploading' ? 'Upload progress' : 'Processing progress'}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
              className="h-2 w-full overflow-hidden rounded-full bg-muted"
            >
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${percent}%` }} />
            </div>
          )}
          {state.phase === 'processing' && (
            <p className="text-xs text-muted-foreground">The server converts the video for phones. This takes 1 to 3 minutes.</p>
          )}
          <button type="button" onClick={cancel} className="rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted">
            Cancel
          </button>
        </div>
      )}

      {state.phase === 'rejected' && (
        <div role="alert" className="space-y-1 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          <p className="font-medium">This file cannot be used: {state.fileName}</p>
          <ul className="list-disc space-y-0.5 pl-5">
            {state.errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {state.phase === 'failed' && (
        <UploadFailure fileName={state.fileName} error={state.error} retryable={state.retryable} disabled={disabled} onRetry={retry} />
      )}

      {state.phase === 'done' && showReady && (
        <p
          ref={readyRef}
          tabIndex={-1}
          className="flex items-center gap-2 rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-800 focus:outline-none focus:ring-2 focus:ring-primary dark:border-green-800 dark:bg-green-950 dark:text-green-200"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          {readyText(languageName)}
        </p>
      )}

      {warnings.length > 0 && (
        <ul className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          {warnings.map((w) => (
            <li key={w} className="flex gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
